/**
 * Phase 300 — Automation Flags settings panel.
 *
 * Renders all 21+ flags grouped by engine with toggles, descriptions,
 * and LIVE/UPCOMING badges. Pulls /api/academy/automation-flags on mount
 * (returns flags + defaults + schema with descriptions), persists via
 * PUT on every toggle (optimistic UI, rolls back on error).
 *
 * Grouping rule: the FLAG_GROUPS map below mirrors the 5 "Engines" in
 * the PRD. New flags added to the backend that AREN'T listed here fall
 * into the "Other" group so the UI stays forward-compatible.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Sparkles, Zap, Eye, IndianRupee, MessageCircle, Settings as SettingsIcon } from 'lucide-react';
import { toast } from 'sonner';

// Engine grouping — single source of truth shared with the backend's
// AutomationFlags pydantic schema (see services/feature_flags.py).
const FLAG_GROUPS = [
  {
    key: 'intake',
    label: 'Intake Engine',
    description: 'Hands-off ways students get into the system.',
    icon: Zap,
    accent: 'text-cyan-600 bg-cyan-50',
    flags: [
      'enable_whatsapp_onboarding',
      'enable_ocr_document_scan',
      'enable_voice_to_crm_logs',
      'enable_magic_link_kiosks',
    ],
  },
  {
    key: 'autopilot',
    label: 'Autopilot Engine',
    description: 'Closed-loop attendance, RSVP, churn prediction.',
    icon: Eye,
    accent: 'text-lime-700 bg-lime-50',
    flags: [
      'enable_ai_facial_attendance',
      'enable_qr_self_checkin',
      'enable_geofence_staff_tracking',
      'enable_whatsapp_rsvps',
      'enable_churn_prediction_winbacks',
      'enable_post_session_nps',
      'enable_peer_kudos_aggregator',
    ],
  },
  {
    key: 'financial',
    label: 'Financial Engine',
    description: 'Auto-billing, payroll, receipt scanning, invoicing.',
    icon: IndianRupee,
    accent: 'text-emerald-700 bg-emerald-50',
    flags: [
      'enable_upi_autobilling',
      'enable_pay_per_session_billing',
      'enable_automated_payroll',
      'enable_ai_receipt_scanner',
      'enable_automated_pdf_invoicing',
    ],
  },
  {
    key: 'communication',
    label: 'Communication Engine',
    description: 'Reminders, reputation recovery, weekly digests.',
    icon: MessageCircle,
    accent: 'text-rose-700 bg-rose-50',
    flags: [
      'enable_smart_reminders',
      'enable_ai_reputation_recovery',
      'enable_weekly_pulse_digest',
    ],
  },
  {
    key: 'operational',
    label: 'Operational Engine',
    description: 'Session closure sweepers, birthday widget, etc.',
    icon: SettingsIcon,
    accent: 'text-indigo-700 bg-indigo-50',
    flags: [
      'enable_auto_session_closure',
      'enable_birthday_widget',
    ],
  },
];

// Pretty-format a flag name from snake_case → Title Case.
function _humanLabel(flagKey) {
  return flagKey
    .replace(/^enable_/, '')
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function FlagRow({ flag, value, description, isLive, disabled, onToggle }) {
  return (
    <div
      className="flex items-start gap-3 px-3 py-3 hover:bg-slate-50 rounded-lg transition-colors"
      data-testid={`automation-flag-row-${flag}`}
    >
      <div className="flex-1 min-w-0 pr-3">
        <div className="flex items-center gap-2 mb-0.5">
          <p className="text-sm font-semibold text-slate-900 truncate">{_humanLabel(flag)}</p>
          {isLive && (
            <Badge className="text-[9px] bg-emerald-100 text-emerald-700 border-emerald-300 px-1.5 py-0">
              LIVE
            </Badge>
          )}
          {!isLive && !value && (
            <Badge variant="outline" className="text-[9px] px-1.5 py-0">UPCOMING</Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
      </div>
      <Switch
        checked={!!value}
        disabled={disabled}
        onCheckedChange={(next) => onToggle(flag, next)}
        data-testid={`automation-flag-switch-${flag}`}
      />
    </div>
  );
}

export default function AutomationFlagsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // flag currently flipping

  const fetchFlags = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/academy/automation-flags');
      setData(r.data);
    } catch (err) {
      toast.error('Could not load automation flags');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFlags(); }, [fetchFlags]);

  const handleToggle = useCallback(async (flag, next) => {
    if (!data) return;
    setSaving(flag);
    // Optimistic update
    const previous = data.flags[flag];
    setData({ ...data, flags: { ...data.flags, [flag]: next } });
    try {
      const r = await api.put('/academy/automation-flags', { [flag]: next });
      // Use server's canonical merged response so we stay in sync.
      setData({ ...data, flags: r.data.flags });
      toast.success(`${_humanLabel(flag)} ${next ? 'enabled' : 'disabled'}`);
    } catch (err) {
      // Roll back optimistic state on failure.
      setData((cur) => cur ? { ...cur, flags: { ...cur.flags, [flag]: previous } } : cur);
      toast.error(err?.response?.data?.detail || 'Could not save — try again');
    } finally {
      setSaving(null);
    }
  }, [data]);

  // Build the catch-all "Other" group for any new server-side flags not yet
  // mapped into FLAG_GROUPS (forward-compatibility while UI catches up).
  const groupsWithOther = useMemo(() => {
    if (!data) return FLAG_GROUPS;
    const known = new Set(FLAG_GROUPS.flatMap((g) => g.flags));
    const serverKeys = Object.keys(data.flags || {});
    const other = serverKeys.filter((k) => !known.has(k));
    if (other.length === 0) return FLAG_GROUPS;
    return [...FLAG_GROUPS, {
      key: 'other',
      label: 'Other',
      description: 'New automation flags pending UI grouping.',
      icon: Sparkles,
      accent: 'text-slate-700 bg-slate-50',
      flags: other,
    }];
  }, [data]);

  if (loading || !data) {
    return (
      <div className="space-y-3 max-w-4xl" data-testid="automation-flags-loading">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  // A flag is "live" when its server-side default is True (the 5 already-
  // shipped features). We treat that as a styling cue, not a lock — owners
  // can still toggle them off if they want to pause a feature.
  const isLiveFlag = (flag) => !!(data.defaults || {})[flag];
  const enabledCount = Object.values(data.flags || {}).filter(Boolean).length;
  const totalCount = Object.keys(data.flags || {}).length;

  return (
    <div className="space-y-4 max-w-4xl" data-testid="automation-flags-panel" id="automation-flags">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-indigo-600" /> Automation Flags
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
            Toggle individual automation modules on or off for your academy. Flags marked <span className="font-semibold text-emerald-700">LIVE</span> are
            already shipped and turned ON by default. Future automations stay OFF until you opt in.
          </p>
        </div>
        <Badge variant="outline" className="text-xs whitespace-nowrap shrink-0" data-testid="automation-flags-summary">
          {enabledCount} of {totalCount} enabled
        </Badge>
      </div>

      {groupsWithOther.map((group) => {
        const Icon = group.icon;
        const groupEnabled = group.flags.filter((f) => data.flags[f]).length;
        return (
          <Card key={group.key} data-testid={`automation-group-${group.key}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <span className={`inline-flex items-center justify-center w-7 h-7 rounded-md ${group.accent}`}>
                    <Icon className="h-4 w-4" />
                  </span>
                  {group.label}
                </CardTitle>
                <Badge variant="secondary" className="text-[10px]" data-testid={`automation-group-count-${group.key}`}>
                  {groupEnabled}/{group.flags.length}
                </Badge>
              </div>
              <CardDescription className="text-xs">{group.description}</CardDescription>
            </CardHeader>
            <CardContent className="pt-1 pb-2 divide-y divide-slate-100">
              {group.flags.map((flag) => {
                const schemaEntry = (data.schema || {})[flag] || {};
                return (
                  <FlagRow
                    key={flag}
                    flag={flag}
                    value={data.flags[flag]}
                    description={schemaEntry.description || 'No description available.'}
                    isLive={isLiveFlag(flag)}
                    disabled={saving === flag}
                    onToggle={handleToggle}
                  />
                );
              })}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
